import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';

import { useInView } from 'react-intersection-observer';
import Odometer from 'odometer';
import 'odometer/themes/odometer-theme-default.css';

import AppContext from '@contexts/AppContext';

import PropertyCard from '@components/properties/card/PropertyCard';
import HomepageSearch from './components/HomepageSearch';
import Footer from '@components/Footer';
import ValuationModal from '@components/ValuationModal';
import CallbackModal from '@components/CallbackModal';
import { getProductList } from '@services/product';
import usePropertySortFn from '@hooks/usePropertySortFn';

const MAX_PROPERTIES = 3;

export default function HomePage() {
    const { agencyData: { agencyId } } = useContext(AppContext);
    const [valuationOpen, setValuationOpen] = useState(false);
    const [callbackOpen, setCallbackOpen] = useState(false);
    const [latestProperties, setLatestProperties] = useState(null);

    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: true,
        rootMargin: '50px'
    });

    const sortFn = usePropertySortFn()

    /*
        Effect:
        Gets properties on mount.
    */
    useEffect(() => {
        async function getProperties() {
            const propertiesRes = await getProductList(agencyId, MAX_PROPERTIES)
            setLatestProperties(propertiesRes.sort(sortFn));
        } 
        getProperties();
    }, []);

    useEffect(() => {
        if (inView) {
            let el = document.querySelector('.js-number');
            let od = new Odometer({ el, duration: 5000 });
            od.update(3000000);
        }
    }, [inView]);

    return (
        <>
            <ValuationModal open={valuationOpen} handleModalClose={() => setValuationOpen(false)} />
            <CallbackModal open={callbackOpen} handleModalClose={() => setCallbackOpen(false)} />
            <HomepageSearch />
            <section className="py-24 bg-gray-100">
                <div className="mx-4">
                    <div className="flex justify-center gap-32 gap-y-16 flex-wrap max-w-lg-container mx-auto">
                        <article className="flex flex-col items-center text-center font-semibold text-xl space-y-4">
                            <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="var(--color-primary-agency)"
                                viewBox="0 0 480 480">
                                <path d="M472,432h-24V280c-0.003-4.418-3.588-7.997-8.006-7.994c-2.607,0.002-5.05,1.274-6.546,3.41l-112,160
                                    c-2.532,3.621-1.649,8.609,1.972,11.14c1.343,0.939,2.941,1.443,4.58,1.444h104v24c0,4.418,3.582,8,8,8s8-3.582,8-8v-24h24
                                    c4.418,0,8-3.582,8-8S476.418,432,472,432z M432,432h-88.64L432,305.376V432z"/>
                                <path d="M328,464h-94.712l88.056-103.688c0.2-0.238,0.387-0.486,0.56-0.744c16.566-24.518,11.048-57.713-12.56-75.552
                                    c-28.705-20.625-68.695-14.074-89.319,14.631C212.204,309.532,207.998,322.597,208,336c0,4.418,3.582,8,8,8s8-3.582,8-8
                                    c-0.003-26.51,21.486-48.002,47.995-48.005c10.048-0.001,19.843,3.151,28.005,9.013c16.537,12.671,20.388,36.007,8.8,53.32
                                    l-98.896,116.496c-2.859,3.369-2.445,8.417,0.924,11.276c1.445,1.226,3.277,1.899,5.172,1.9h112c4.418,0,8-3.582,8-8
                                    S332.418,464,328,464z"/>
                                <path d="M216.176,424.152c0.167-4.415-3.278-8.129-7.693-8.296c-0.001,0-0.002,0-0.003,0
                                    C104.11,411.982,20.341,328.363,16.28,224H48c4.418,0,8-3.582,8-8s-3.582-8-8-8H16.28C20.283,103.821,103.82,20.287,208,16.288
                                    V40c0,4.418,3.582,8,8,8s8-3.582,8-8V16.288c102.754,3.974,185.686,85.34,191.616,188l-31.2-31.2
                                    c-3.178-3.07-8.242-2.982-11.312,0.196c-2.994,3.1-2.994,8.015,0,11.116l44.656,44.656c0.841,1.018,1.925,1.807,3.152,2.296
                                    c0.313,0.094,0.631,0.172,0.952,0.232c0.549,0.198,1.117,0.335,1.696,0.408c0.08,0,0.152,0,0.232,0c0.08,0,0.152,0,0.224,0
                                    c0.609-0.046,1.211-0.164,1.792-0.352c0.329-0.04,0.655-0.101,0.976-0.184c1.083-0.385,2.069-1.002,2.888-1.808l45.264-45.248
                                    c3.069-3.178,2.982-8.242-0.196-11.312c-3.1-2.994-8.015-2.994-11.116,0l-31.976,31.952
                                    C425.933,90.37,331.38,0.281,216.568,0.112C216.368,0.104,216.2,0,216,0s-0.368,0.104-0.568,0.112
                                    C96.582,0.275,0.275,96.582,0.112,215.432C0.112,215.632,0,215.8,0,216s0.104,0.368,0.112,0.568
                                    c0.199,115.917,91.939,210.97,207.776,215.28h0.296C212.483,431.847,216.013,428.448,216.176,424.152z"/>
                                <path d="M323.48,108.52c-3.124-3.123-8.188-3.123-11.312,0L226.2,194.48c-6.495-2.896-13.914-2.896-20.408,0l-40.704-40.704
                                    c-3.178-3.069-8.243-2.981-11.312,0.197c-2.994,3.1-2.994,8.015,0,11.115l40.624,40.624c-5.704,11.94-0.648,26.244,11.293,31.947
                                    c9.165,4.378,20.095,2.501,27.275-4.683c7.219-7.158,9.078-18.118,4.624-27.256l85.888-85.888
                                    C326.603,116.708,326.603,111.644,323.48,108.52z M221.658,221.654c-0.001,0.001-0.001,0.001-0.002,0.002
                                    c-3.164,3.025-8.148,3.025-11.312,0c-3.125-3.124-3.125-8.189-0.002-11.314c3.124-3.125,8.189-3.125,11.314-0.002
                                    C224.781,213.464,224.781,218.53,221.658,221.654z"/>
                            </svg>
                            <p>
                                24/7 <br />
                                bidding
                            </p>
                        </article>
                        <article className="flex flex-col items-center text-center font-semibold text-xl space-y-4">
                            <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="var(--color-primary-agency)"
                                viewBox="0 0 54 54">
                                <path d="M43,20.113V14.5C43,6.505,35.822,0,27,0S11,6.505,11,14.5v5.613c-3.401,0.586-6,3.55-6,7.117v19.542
                                    C5,50.757,8.243,54,12.229,54h29.542C45.757,54,49,50.757,49,46.771V27.229C49,23.663,46.401,20.699,43,20.113z M13,14.5
                                    C13,7.607,19.28,2,27,2s14,5.607,14,12.5V20H13V14.5z M47,46.771C47,49.654,44.654,52,41.771,52H12.229C9.346,52,7,49.654,7,46.771
                                    V27.229C7,24.346,9.346,22,12.229,22h29.542C44.654,22,47,24.346,47,27.229V46.771z"/>
                                <path d="M27,28c-2.206,0-4,1.794-4,4v6c0,2.206,1.794,4,4,4s4-1.794,4-4v-6C31,29.794,29.206,28,27,28z M29,38c0,1.103-0.897,2-2,2
                                    s-2-0.897-2-2v-6c0-1.103,0.897-2,2-2s2,0.897,2,2V38z"/>
                            </svg>
                            <p>
                                Secure <br />
                                Sale
                            </p>
                        </article>
                        <article className="flex flex-col items-center text-center font-semibold text-xl space-y-4">
                            <svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="var(--color-primary-agency)"
                                viewBox="0 0 477.867 477.867">
                                <path d="M238.933,0C106.974,0,0,106.974,0,238.933s106.974,238.933,238.933,238.933s238.933-106.974,238.933-238.933
                                    C477.726,107.033,370.834,0.141,238.933,0z M238.933,443.733c-113.108,0-204.8-91.692-204.8-204.8s91.692-204.8,204.8-204.8
                                    s204.8,91.692,204.8,204.8C443.611,351.991,351.991,443.611,238.933,443.733z"/>
                                <path d="M370.046,141.534c-6.614-6.388-17.099-6.388-23.712,0v0L187.733,300.134l-56.201-56.201
                                    c-6.548-6.78-17.353-6.967-24.132-0.419c-6.78,6.548-6.967,17.353-0.419,24.132c0.137,0.142,0.277,0.282,0.419,0.419
                                    l68.267,68.267c6.664,6.663,17.468,6.663,24.132,0l170.667-170.667C377.014,158.886,376.826,148.082,370.046,141.534z"/>
                            </svg>
                            <p>
                                Transparent <br />
                                Process
                            </p>
                        </article>
                        <article className="flex flex-col items-center text-center font-semibold text-xl space-y-4">
                            <svg viewBox="0 -28 512.001 512" xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="var(--color-primary-agency)">
                                <path d="m256 455.515625c-7.289062 0-14.316406-2.640625-19.792969-7.4375-20.683593-18.085937-40.625-35.082031-58.21875-50.074219l-.089843-.078125c-51.582032-43.957031-96.125-81.917969-127.117188-119.3125-34.644531-41.804687-50.78125-81.441406-50.78125-124.742187 0-42.070313 14.425781-80.882813 40.617188-109.292969 26.503906-28.746094 62.871093-44.578125 102.414062-44.578125 29.554688 0 56.621094 9.34375 80.445312 27.769531 12.023438 9.300781 22.921876 20.683594 32.523438 33.960938 9.605469-13.277344 20.5-24.660157 32.527344-33.960938 23.824218-18.425781 50.890625-27.769531 80.445312-27.769531 39.539063 0 75.910156 15.832031 102.414063 44.578125 26.191406 28.410156 40.613281 67.222656 40.613281 109.292969 0 43.300781-16.132812 82.9375-50.777344 124.738281-30.992187 37.398437-75.53125 75.355469-127.105468 119.308594-17.625 15.015625-37.597657 32.039062-58.328126 50.167969-5.472656 4.789062-12.503906 7.429687-19.789062 7.429687zm-112.96875-425.523437c-31.066406 0-59.605469 12.398437-80.367188 34.914062-21.070312 22.855469-32.675781 54.449219-32.675781 88.964844 0 36.417968 13.535157 68.988281 43.882813 105.605468 29.332031 35.394532 72.960937 72.574219 123.476562 115.625l.09375.078126c17.660156 15.050781 37.679688 32.113281 58.515625 50.332031 20.960938-18.253907 41.011719-35.34375 58.707031-50.417969 50.511719-43.050781 94.136719-80.222656 123.46875-115.617188 30.34375-36.617187 43.878907-69.1875 43.878907-105.605468 0-34.515625-11.605469-66.109375-32.675781-88.964844-20.757813-22.515625-49.300782-34.914062-80.363282-34.914062-22.757812 0-43.652344 7.234374-62.101562 21.5-16.441406 12.71875-27.894532 28.796874-34.609375 40.046874-3.453125 5.785157-9.53125 9.238282-16.261719 9.238282s-12.808594-3.453125-16.261719-9.238282c-6.710937-11.25-18.164062-27.328124-34.609375-40.046874-18.449218-14.265626-39.34375-21.5-62.097656-21.5zm0 0"/>
                            </svg>
                            <p>
                                Personal <br />
                                Service
                            </p>
                        </article>
                    </div>
                </div>
            </section>
            <div className="mx-4">
                <section className="text-center my-16 max-w-[1150px] mx-auto">
                    <header className="space-y-6">
                        <p className="text-xl tracking-wide font-semibold">Welcome to our online auction platform</p>
                        <h2 className="tracking-wide text-3xl md:text-5xl font-bold">The <span className="text-primary-agency">new way</span> of buying property</h2>
                    </header>
                    <p className="mt-16 text-lg md:text-2xl">
                        From residential to commercial, properties of all types are now being sold all across the country through online auctions.
                    </p>
                    <p className="mt-8 text-lg md:text-2xl">
                        We offer the modern method of auction, which combines the best aspects of traditional auction and private treaty sales, making
                        it a more accessible auction method. This method takes place online, opening the market to greater levels of interest and activity.
                    </p>
                </section>
            </div>
            <section className="py-20 bg-gray-100">
                <div className="max-w-4xl mx-auto">
                    <div className="flex flex-col items-center mx-4 text-center">
                        <p className="text-5xl md:text-8xl text-primary-agency font-bold" ref={ref}>
                            <span className="js-number">0,000,000</span>+
                            </p>
                        <p className="mt-6 text-xl md:text-2xl text-primary-agency font-bold leading-6">
                            Properties Sold <br /> 
                            via online auctions
                        </p>
                        <p className="mt-10 font-semibold text-xl">
                            More people are choosing to buy and sell property through this method.
                        </p>
                        <button className="mt-12 py-3 px-16 bg-secondary-agency text-white text-2xl font-semibold rounded-full" onClick={() => setCallbackOpen(true)}>Sell my property</button>
                    </div>
                </div>
            </section>
            <section className="relative h-0 pb-[200%] xs:pb-[150%] lg:pb-[60%]">
                <div className="home-c-stat-section absolute inset-0 py-20 px-4 text-center">
                    <div className="absolute inset-0 bg-white bg-opacity-70"></div>
                    <div className="relative max-w-[1200px] mx-auto">
                        <header className="space-y-8">
                            <h2 className="tracking-wide font-bold text-3xl lg:text-5xl">It's as <span className="text-primary-agency">easy</span> as 321</h2>
                            <p className="text-lg lg:text-xl font-semibold tracking-wide">Whether you are buying or selling a auction, we can help you move forward.</p>
                        </header>
                        <div className="relative mt-12">
                            <div className="hidden lg:block absolute right-[-10px] top-[-20px] w-24 h-8 border-r-8 border-t-8 border-primary-agency" />
                            <div className="hidden lg:block absolute left-[5px] bottom-[-20px] w-24 h-8 border-b-8 border-l-8 border-primary-agency"></div>
                            <p className="text-2xl sm:text-3xl lg:text-5xl font-bold">9.5 out of 10 buyers and sellers have a successful moving experience using online auctions.</p>
                        </div>
                        <div className="flex flex-col lg:flex-row justify-center items-center mt-16 space-y-8 lg:space-y-0 lg:space-x-12">
                            <Link to="/buying" className="py-2 px-20 text-xl text-secondary-agency border-2 border-secondary-agency rounded-full font-semibold">Buying</Link>
                            <Link to="/selling" className="py-2 px-20 text-xl text-secondary-agency border-2 border-secondary-agency rounded-full font-semibold">Selling</Link>
                        </div>
                    </div>
                </div>
            </section>
            <section className="my-24">
                <header className="text-center font-bold">
                    <h2 className="text-3xl md:text-5xl font-bold tracking-wide">Creating <span className="text-primary-agency">success</span> for you</h2>
                    <p className="mt-6 max-w-lg-container mx-auto text-lg md:text-2xl text-gray-600 tracking-wide text-center">
                        From the second you choose online auctions to the moment your property is sold, we guide you every
                        step of the way to ensure a fast, esy and hassle-free moving experience.
                    </p>
                </header>
                <div className="max-w-[920px] mx-auto mt-20">
                    <div className="flex justify-center flex-wrap gap-5 my-8">
                        <article
                        data-aos="fade-up"
                        data-aos-duration="600"
                        data-aos-once="true"
                        className="min-w-[280px] h-[305px] flex flex-col items-center p-20 pt-16 border border-gray-300 rounded-md shadow-lg">
                            <div className="relative top-1/2 transform -translate-y-1/2 text-lg text-center space-y-4">
                                <p className="text-6xl font-bold text-primary-agency">56</p>
                                <p className="font-bold text-center">
                                    Day <br /> 
                                    Completion
                                </p>
                            </div>
                        </article>
                        <article
                        data-aos="fade-up"
                        data-aos-delay="600"
                        data-aos-duration="600"
                        data-aos-once="true"
                        className="min-w-[280px] h-[305px] flex flex-col items-center p-20 pt-16 border border-gray-300 rounded-md shadow-lg">
                            <div className="relative top-1/2 transform -translate-y-1/2 text-lg text-center space-y-4">
                                <p className="text-6xl font-bold text-primary-agency">95%</p>
                                <p className="font-bold text-center">
                                    Completion <br />
                                    Rate
                                </p>
                            </div>
                        </article>
                        <article
                        data-aos="fade-up"
                        data-aos-delay="1200"
                        data-aos-duration="600"
                        data-aos-once="true"
                        className="min-w-[280px] h-[305px] flex flex-col items-center p-20 pt-16 border border-gray-300 rounded-md shadow-lg">
                            <div className="relative top-1/2 transform -translate-y-2/5 text-lg text-center space-y-4">
                                <p className="text-6xl font-bold text-primary-agency">4x</p>
                                <p className="font-bold text-center">
                                    Faster than <br />
                                    Private Treaty <br />
                                    Sales (average)
                                </p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>
            <div className="border-t border-b border-gray-300 px-4">
                <section className="flex flex-col lg:flex-row justify-between items-center max-w-lg-container mx-auto py-10 space-y-8 lg:space-y-0 lg:space-x-4">
                    <div className="text-center lg:text-left">
                        <header>
                            <h2 className="tracking-wide text-3xl lg:text-[2.75rem] font-bold">How much is my home worth?</h2>
                        </header>
                        <p className="mt-8 lg:mt-12 max-w-[650px] text-lg lg:text-xl text-gray-600 tracking-wide">If you're ready to sell at auction or just plain curious, the next step is to get an accurate valuation from one of our local property experts.</p>
                        <button className="mt-12 py-3 px-6 text-xl text-white bg-secondary-agency rounded-full" onClick={() => setValuationOpen(true)}>Get a free valuation</button>
                    </div>
                    <img className="lg:w-1/3 xl:w-2/5 rounded-lg" src="/images/home-worth.jpeg" alt="" />
                </section>
            </div>
            {latestProperties && latestProperties.length > 0 && (
                <div className="flex flex-col items-center pt-5 px-3 lg:px-12 pb-12 my-24">
                    <h1 className="mb-16 text-center text-3xl lg:text-5xl tracking-wide font-bold">Your local properties</h1>
                    <div className="property-grid grid max-w-lg-container w-full gap-4">
                        {latestProperties.map((property) => 
                            <PropertyCard 
                                key={property.productId}
                                {...property}
                                publishDate={new Date(property.publishDate).getTime()}
                                bidEndTime={new Date(property.bidEndTime).getTime()} />)}
                    </div>
                </div>
            )}
            <Footer />
        </>
    )
}